import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";

// Add any built-in background image URLs here. Relative URLs are easiest because
// they stay same-origin for PNG export. Cross-origin URLs also work when that
// server allows CORS image fetching.
const BACKGROUND_IMAGE_URLS = [
  "/newsets/images/backgrounds/woodbg2.webp",
];

const GRADIENT_BACKGROUNDS = [
  // {
  //   id: "tavern",
  //   name: "Tavern",
  //   css: "radial-gradient(circle at 50% 42%, #7a4722 0%, #3c1d0d 40%, #160b08 100%)",
  //   colors: ["#7a4722", "#3c1d0d", "#160b08"],
  //   kind: "radial",
  // },
  {
    id: "arcane",
    name: "Arcane",
    css: "linear-gradient(135deg, #35115f 0%, #6827a8 48%, #171044 100%)",
    colors: ["#35115f", "#6827a8", "#171044"],
    kind: "linear",
  },
  {
    id: "frost",
    name: "Frost",
    css: "linear-gradient(135deg, #09203f 0%, #16697a 48%, #53b8c7 100%)",
    colors: ["#09203f", "#16697a", "#53b8c7"],
    kind: "linear",
  },
  {
    id: "ember",
    name: "Ember",
    css: "linear-gradient(135deg, #2b0b08 0%, #8a241c 48%, #df7b24 100%)",
    colors: ["#2b0b08", "#8a241c", "#df7b24"],
    kind: "linear",
  },
  {
    id: "midnight",
    name: "Midnight",
    css: "linear-gradient(145deg, #090b16 0%, #111a32 50%, #05060b 100%)",
    colors: ["#090b16", "#111a32", "#05060b"],
    kind: "linear",
  },
];

const IMAGE_BACKGROUNDS = BACKGROUND_IMAGE_URLS.map((src, index) => ({
  id: `image-${index}`,
  name: `Image ${index + 1}`,
  src,
  kind: "image",
}));

const BACKGROUNDS = [...GRADIENT_BACKGROUNDS, ...IMAGE_BACKGROUNDS];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const getSceneConfig = (count) => {
  if (count <= 1) {
    return { width: 900, height: 1100, baseCardWidth: 610 };
  }

  if (count === 2) {
    return { width: 1200, height: 950, baseCardWidth: 540 };
  }

  if (count === 3) {
    return { width: 1450, height: 950, baseCardWidth: 470 };
  }

  return { width: 1600, height: 900, baseCardWidth: 430 };
};

const createInitialCardLayout = (assets, scene) => {
  const count = assets.length;

  return assets.map((asset, index) => {
    let x = scene.width / 2;
    let y = scene.height * 0.52;
    let rotation = 0;
    let scale = 0.96;

    if (count === 2) {
      x = scene.width * (index === 0 ? 0.315 : 0.695);
      y = scene.height * (index === 0 ? 0.56 : 0.41);
      rotation = index === 0 ? -6 : 6;
      scale = 0.98;
    } else if (count === 3) {
      x = scene.width * [0.2, 0.5, 0.8][index];
      y = scene.height * 0.535;
      rotation = [-8, 0, 8][index];
      scale = 0.98;
    } else if (count > 3) {
      const left = scene.width * 0.10;
      const right = scene.width * 0.90;
      const step = count > 1 ? (right - left) / (count - 1) : 0;
      x = left + step * index;
      y = scene.height * 0.56 + (index % 2 === 0 ? -14 : 18);
      rotation = (index - (count - 1) / 2) * 6;
      scale = clamp(0.74 - Math.max(0, count - 4) * 0.065, 0.5, 0.84);
    }

    return {
      ...asset,
      kind: "card",
      x,
      y,
      rotation,
      scale,
      z: index + 1,
    };
  });
};

const createTextElement = (scene, index, topZ) => ({
  id: `text-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  kind: "text",
  label: `Text ${index}`,
  text: "Your text",
  x: scene.width * 0.5,
  y: scene.height * 0.22,
  rotation: 0,
  fontSize: Math.round(scene.width * 0.045),
  color: "#ffffff",
  z: topZ + 1,
});

const getElementDisplayLabel = (element) => {
  if (element.kind === "text") {
    const snippet = (element.text || "Text")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 22);
    return snippet ? `${element.label}: ${snippet}` : element.label;
  }

  return element.label;
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Unable to load ${src}`));
    image.src = src;
  });

const getCardProxyUrl = (src) => {
  try {
    const url = new URL(src, window.location.origin);
    if (!url.pathname.startsWith("/cards/")) return null;

    const filename = url.pathname.split("/").pop();
    if (!filename) return null;

    return `/api/card/share-editor-image.php?file=${encodeURIComponent(filename)}`;
  } catch {
    return null;
  }
};

const loadOriginCleanImage = async (src) => {
  const proxyUrl = getCardProxyUrl(src);
  const candidates = [proxyUrl, src].filter(Boolean);
  let lastError = null;

  for (const candidate of candidates) {
    try {
      const response = await fetch(candidate, {
        credentials: "same-origin",
        cache: "force-cache",
      });

      if (!response.ok || response.type === "opaque") {
        throw new Error(`Image request failed (${response.status || "opaque"}).`);
      }

      const blob = await response.blob();
      if (!blob.type.startsWith("image/")) {
        throw new Error("The image request did not return an image.");
      }

      const objectUrl = window.URL.createObjectURL(blob);
      try {
        const image = await loadImage(objectUrl);
        return { image, objectUrl };
      } catch (error) {
        window.URL.revokeObjectURL(objectUrl);
        throw error;
      }
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error(`Unable to load ${src}`);
};

const paintVignette = (context, scene, strength) => {
  if (strength <= 0) return;

  const vignette = context.createRadialGradient(
    scene.width / 2,
    scene.height / 2,
    Math.min(scene.width, scene.height) * 0.28,
    scene.width / 2,
    scene.height / 2,
    Math.max(scene.width, scene.height) * 0.74,
  );
  vignette.addColorStop(0, "rgba(0,0,0,0)");
  vignette.addColorStop(0.58, `rgba(0,0,0,${strength * 0.08})`);
  vignette.addColorStop(0.82, `rgba(0,0,0,${strength * 0.42})`);
  vignette.addColorStop(1, `rgba(0,0,0,${strength})`);
  context.fillStyle = vignette;
  context.fillRect(0, 0, scene.width, scene.height);
};

const paintPresetBackground = (context, preset, scene) => {
  let gradient;

  if (preset.kind === "radial") {
    gradient = context.createRadialGradient(
      scene.width / 2,
      scene.height * 0.42,
      40,
      scene.width / 2,
      scene.height * 0.5,
      Math.max(scene.width, scene.height) * 0.7,
    );
  } else {
    gradient = context.createLinearGradient(0, 0, scene.width, scene.height);
  }

  preset.colors.forEach((color, index) => {
    gradient.addColorStop(index / (preset.colors.length - 1), color);
  });

  context.fillStyle = gradient;
  context.fillRect(0, 0, scene.width, scene.height);
};

const drawImageCover = (context, image, scene) => {
  const imageRatio = image.naturalWidth / image.naturalHeight;
  const sceneRatio = scene.width / scene.height;

  let sourceWidth = image.naturalWidth;
  let sourceHeight = image.naturalHeight;
  let sourceX = 0;
  let sourceY = 0;

  if (imageRatio > sceneRatio) {
    sourceWidth = image.naturalHeight * sceneRatio;
    sourceX = (image.naturalWidth - sourceWidth) / 2;
  } else {
    sourceHeight = image.naturalWidth / sceneRatio;
    sourceY = (image.naturalHeight - sourceHeight) / 2;
  }

  context.drawImage(
    image,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    scene.width,
    scene.height,
  );
};

const paintBackgroundImage = (context, image, scene, blur) => {
  if (!blur) {
    drawImageCover(context, image, scene);
    return;
  }

  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = scene.width;
  tempCanvas.height = scene.height;
  const tempContext = tempCanvas.getContext("2d");
  if (!tempContext) {
    drawImageCover(context, image, scene);
    return;
  }

  drawImageCover(tempContext, image, scene);

  const overscan = blur * 2;
  context.save();
  context.filter = `blur(${blur}px)`;
  context.drawImage(
    tempCanvas,
    -overscan,
    -overscan,
    scene.width + overscan * 2,
    scene.height + overscan * 2,
  );
  context.restore();
};

const drawTextElement = (context, element) => {
  const fontSize = element.fontSize;
  const lineHeight = Math.round(fontSize * 1.08);

  context.save();
  context.translate(element.x, element.y);
  context.rotate((element.rotation * Math.PI) / 180);
  context.textAlign = "center";
  context.textBaseline = "top";
  context.fillStyle = element.color || "#ffffff";
  context.shadowColor = "rgba(0,0,0,0.7)";
  context.shadowBlur = Math.max(10, fontSize * 0.28);
  context.font = `800 ${fontSize}px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;

  const lines = String(element.text || "").split("\n");
  const totalHeight = lineHeight * lines.length;
  const startY = -totalHeight / 2;

  lines.forEach((line, index) => {
    context.fillText(line, 0, startY + index * lineHeight);
  });

  context.restore();
};

const ShareCardEditor = ({ show, onHide, cardInfo, tokens = [] }) => {
  const stageRef = useRef(null);
  const dragRef = useRef(null);

  const assets = useMemo(
    () => [
      {
        id: `main-${cardInfo.cardId2}`,
        src: `/cards/${cardInfo.cardId2}.${cardInfo.cardFormat}`,
        label: cardInfo.cardName || "Main card",
      },
      ...tokens.map((token, index) => ({
        id: `token-${token.id}-${index}`,
        src: `/cards/${token.id}.${token.format}`,
        label: token.class || `Token ${index + 1}`,
      })),
    ],
    [cardInfo.cardFormat, cardInfo.cardId2, cardInfo.cardName, tokens],
  );

  const scene = useMemo(() => getSceneConfig(assets.length), [assets.length]);
  const [elements, setElements] = useState(() => createInitialCardLayout(assets, scene));
  const [selectedId, setSelectedId] = useState(assets[0]?.id ?? null);
const [backgroundId, setBackgroundId] = useState(
  IMAGE_BACKGROUNDS[0]?.id || GRADIENT_BACKGROUNDS[0].id
);
  const [customBackground, setCustomBackground] = useState(null);
  const [showTitle, setShowTitle] = useState(true);
  const [showBrand, setShowBrand] = useState(true);
  const [backgroundBlur, setBackgroundBlur] = useState(0);
  const [backgroundDarkness, setBackgroundDarkness] = useState(0.18);
  const [vignetteStrength, setVignetteStrength] = useState(0.42);
  const [stageViewport, setStageViewport] = useState({ width: 0, height: 0 });
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  useEffect(() => {
    if (!show) return;

    const nextLayout = createInitialCardLayout(assets, scene);
    setElements(nextLayout);
    setSelectedId(nextLayout[0]?.id ?? null);
    setExportError("");
    setBackgroundBlur(0);
    setBackgroundDarkness(0.18);
    setVignetteStrength(0.42);
  }, [assets, scene, show]);

  useEffect(() => {
    if (!show || !stageRef.current || typeof ResizeObserver === "undefined") return undefined;

    const updateViewport = () => {
      const rect = stageRef.current?.getBoundingClientRect();
      if (!rect) return;
      setStageViewport({ width: rect.width, height: rect.height });
    };

    updateViewport();
    const observer = new ResizeObserver(updateViewport);
    observer.observe(stageRef.current);
    window.addEventListener("resize", updateViewport);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateViewport);
    };
  }, [show]);

  const selectedElement = elements.find((element) => element.id === selectedId);
  const selectedBackground =
    BACKGROUNDS.find((background) => background.id === backgroundId) || BACKGROUNDS[0];

  const updateElement = (id, updates) => {
    setElements((previous) =>
      previous.map((element) =>
        element.id === id ? { ...element, ...updates } : element,
      ),
    );
  };

  const selectAndRaise = (id) => {
    setSelectedId(id);
    setElements((previous) => {
      const topZ = Math.max(...previous.map((element) => element.z), 0);
      return previous.map((element) =>
        element.id === id ? { ...element, z: topZ + 1 } : element,
      );
    });
  };

  const handlePointerDown = (event, element) => {
    if (event.button !== 0) return;

    event.preventDefault();
    selectAndRaise(element.id);
    event.currentTarget.setPointerCapture?.(event.pointerId);

    dragRef.current = {
      pointerId: event.pointerId,
      id: element.id,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: element.x,
      startY: element.y,
    };
  };

  const handlePointerMove = (event) => {
    const drag = dragRef.current;
    const stage = stageRef.current;
    if (!drag || !stage || drag.pointerId !== event.pointerId) return;

    const rect = stage.getBoundingClientRect();
    const deltaX = ((event.clientX - drag.startClientX) / rect.width) * scene.width;
    const deltaY = ((event.clientY - drag.startClientY) / rect.height) * scene.height;

    updateElement(drag.id, {
      x: clamp(drag.startX + deltaX, -200, scene.width + 200),
      y: clamp(drag.startY + deltaY, -200, scene.height + 200),
    });
  };

  const handlePointerUp = (event) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
  };

  const handleBackgroundUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setExportError("Please choose an image file for the background.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setCustomBackground({
        src: String(reader.result),
        name: file.name,
      });
      setBackgroundId("custom");
      setExportError("");
    };
    reader.onerror = () => {
      setExportError("Could not read that background image.");
    };
    reader.readAsDataURL(file);
  };

  const resetLayout = () => {
    setElements(createInitialCardLayout(assets, scene));
    setSelectedId(assets[0]?.id ?? null);
  };

  const nudgeSelected = (dx, dy) => {
    if (!selectedElement) return;
    updateElement(selectedElement.id, {
      x: selectedElement.x + dx,
      y: selectedElement.y + dy,
    });
  };

  const addTextElement = () => {
    setElements((previous) => {
      const textCount = previous.filter((element) => element.kind === "text").length + 1;
      const topZ = Math.max(...previous.map((element) => element.z), 0);
      const nextText = createTextElement(scene, textCount, topZ);
      setSelectedId(nextText.id);
      return [...previous, nextText];
    });
  };

  const removeSelectedText = () => {
    if (!selectedElement || selectedElement.kind !== "text") return;

    setElements((previous) => {
      const next = previous.filter((element) => element.id !== selectedElement.id);
      setSelectedId(next[0]?.id ?? null);
      return next;
    });
  };

  const exportImage = async () => {
    setIsExporting(true);
    setExportError("");

    const objectUrls = [];

    try {
      const canvas = document.createElement("canvas");
      canvas.width = scene.width;
      canvas.height = scene.height;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Canvas is not available in this browser.");

      if (backgroundId === "custom" && customBackground?.src) {
        const backgroundImage = await loadImage(customBackground.src);
        paintBackgroundImage(context, backgroundImage, scene, backgroundBlur);
      } else if (selectedBackground.kind === "image") {
        const loadedBackground = await loadOriginCleanImage(selectedBackground.src);
        objectUrls.push(loadedBackground.objectUrl);
        paintBackgroundImage(context, loadedBackground.image, scene, backgroundBlur);
      } else {
        paintPresetBackground(context, selectedBackground, scene);
      }

      if (backgroundDarkness > 0) {
        context.fillStyle = `rgba(0,0,0,${backgroundDarkness})`;
        context.fillRect(0, 0, scene.width, scene.height);
      }

      if (vignetteStrength > 0) {
        paintVignette(context, scene, vignetteStrength);
      }

      if (showTitle) {
        const titleFontSize = Math.round(clamp(scene.width * 0.04, 42, 60));
        const creatorFontSize = Math.round(clamp(scene.width * 0.018, 21, 28));
        const left = scene.width * 0.045;
        const top = scene.height * 0.08;

        context.save();
        context.shadowColor = "rgba(0,0,0,0.7)";
        context.shadowBlur = 18;
        context.fillStyle = "#ffffff";
        context.font = `700 ${titleFontSize}px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
        context.fillText(cardInfo.cardName || "Card", left, top);

        if (cardInfo.creatorName) {
          context.shadowBlur = 12;
          context.fillStyle = "rgba(255,255,255,0.78)";
          context.font = `500 ${creatorFontSize}px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
          context.fillText(`by ${cardInfo.creatorName}`, left + 4, top + titleFontSize * 0.72);
        }
        context.restore();
      }

      const orderedElements = [...elements].sort((a, b) => a.z - b.z);

      for (const element of orderedElements) {
        if (element.kind === "text") {
          drawTextElement(context, element);
          continue;
        }

        const loaded = await loadOriginCleanImage(element.src);
        objectUrls.push(loaded.objectUrl);
        const image = loaded.image;
        const width = scene.baseCardWidth * element.scale;
        const aspectRatio = image.naturalHeight / image.naturalWidth;
        const height = width * aspectRatio;

        context.save();
        context.translate(element.x, element.y);
        context.rotate((element.rotation * Math.PI) / 180);
        context.shadowColor = "rgba(0,0,0,0.52)";
        context.shadowBlur = 32;
        context.shadowOffsetY = 18;
        context.drawImage(image, -width / 2, -height / 2, width, height);
        context.restore();
      }

      if (showBrand) {
        context.save();
        context.textAlign = "right";
        context.shadowColor = "rgba(0,0,0,0.7)";
        context.shadowBlur = 8;
        context.fillStyle = "rgba(255,255,255,0.76)";
        context.font = `700 ${Math.round(clamp(scene.width * 0.015, 18, 24))}px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
        context.fillText(
          "HearthCards.net",
          scene.width - scene.width * 0.035,
          scene.height - scene.height * 0.045,
        );
        context.restore();
      }

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (nextBlob) =>
            nextBlob ? resolve(nextBlob) : reject(new Error("Image export failed.")),
          "image/png",
          1,
        );
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const safeName = (cardInfo.cardName || "card")
        .replace(/[^a-z0-9-_]+/gi, "-")
        .replace(/^-+|-+$/g, "")
        .toLowerCase();
      link.href = url;
      link.download = `${safeName || "card"}-share.png`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting custom share image:", error);
      setExportError(
        error.message ||
          "Could not export the image. Make sure share-editor-image.php is installed on the server.",
      );
    } finally {
      objectUrls.forEach((url) => window.URL.revokeObjectURL(url));
      setIsExporting(false);
    }
  };

  const stageMaxWidth =
    assets.length <= 1
      ? "620px"
      : assets.length === 2
        ? "900px"
        : assets.length === 3
          ? "1100px"
          : "100%";

  const stageStyle = {
    aspectRatio: `${scene.width} / ${scene.height}`,
    width: "100%",
    maxWidth: stageMaxWidth,
  };

  const activeBackgroundImage =
    backgroundId === "custom" && customBackground?.src
      ? customBackground.src
      : selectedBackground.kind === "image"
        ? selectedBackground.src
        : null;

  const backgroundLayerStyle = activeBackgroundImage
    ? {
        backgroundImage: `url(${activeBackgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        transform: `scale(${1 + backgroundBlur / 120})`,
        filter: `blur(${backgroundBlur}px)`,
      }
    : {
        background: selectedBackground.css,
        transform: `scale(${1 + backgroundBlur / 180})`,
        filter: backgroundBlur ? `blur(${backgroundBlur * 0.35}px)` : "none",
      };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      dialogClassName="share-editor-modal"
      contentClassName="share-editor-modal-content"
    >
      <Modal.Header closeButton>
        <Modal.Title>Share Image Editor</Modal.Title>
      </Modal.Header>

      <Modal.Body className="share-editor-body">
        <div className="share-editor-layout">
          <div className="share-editor-workspace-wrap">
            <div
              ref={stageRef}
              className="share-editor-stage"
              style={stageStyle}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            >
              <div className="share-editor-background-layer" style={backgroundLayerStyle} />
              <div
                className="share-editor-darken"
                style={{ opacity: clamp(backgroundDarkness, 0, 0.75) }}
              />
              <div
                className="share-editor-vignette"
                style={{ opacity: clamp(vignetteStrength, 0, 1) }}
              />

              {showTitle && (
                <div className="share-editor-title" aria-hidden="true">
                  <div>{cardInfo.cardName}</div>
                  {cardInfo.creatorName && <small>by {cardInfo.creatorName}</small>}
                </div>
              )}

              {[...elements]
                .sort((a, b) => a.z - b.z)
                .map((element) => {
                  if (element.kind === "text") {
                    const fontSizePx = stageViewport.width
                      ? (element.fontSize / scene.width) * stageViewport.width
                      : 28;

                    return (
                      <button
                        type="button"
                        key={element.id}
                        className={`share-editor-text-item ${
                          selectedId === element.id ? "is-selected" : ""
                        }`}
                        style={{
                          left: `${(element.x / scene.width) * 100}%`,
                          top: `${(element.y / scene.height) * 100}%`,
                          zIndex: element.z,
                          transform: `translate(-50%, -50%) rotate(${element.rotation}deg)`,
                        }}
                        onPointerDown={(event) => handlePointerDown(event, element)}
                        onClick={() => selectAndRaise(element.id)}
                        aria-label={`Move ${element.label}`}
                      >
                        <div
                          className="share-editor-text-content"
                          style={{
                            fontSize: `${fontSizePx}px`,
                            color: element.color || "#ffffff",
                          }}
                        >
                          {element.text}
                        </div>
                      </button>
                    );
                  }

                  return (
                    <button
                      type="button"
                      key={element.id}
                      className={`share-editor-card ${
                        selectedId === element.id ? "is-selected" : ""
                      }`}
                      style={{
                        left: `${(element.x / scene.width) * 100}%`,
                        top: `${(element.y / scene.height) * 100}%`,
                        width: `${(scene.baseCardWidth / scene.width) * 100}%`,
                        zIndex: element.z,
                        transform: `translate(-50%, -50%) rotate(${element.rotation}deg) scale(${element.scale})`,
                      }}
                      onPointerDown={(event) => handlePointerDown(event, element)}
                      onClick={() => selectAndRaise(element.id)}
                      aria-label={`Move ${element.label}`}
                    >
                      <img src={element.src} alt={element.label} draggable={false} />
                    </button>
                  );
                })}

              {showBrand && (
                <div className="share-editor-brand" aria-hidden="true">
                  HearthCards.net
                </div>
              )}
            </div>
            <div className="share-editor-hint">
              Drag cards or text to move them. Export size: {scene.width}×{scene.height}px.
            </div>
          </div>

          <aside className="share-editor-controls">
            <section>
              <div className="share-editor-control-title">Background</div>
              <div className="share-editor-backgrounds">
                {BACKGROUNDS.map((background) => (
                  <button
                    type="button"
                    key={background.id}
                    className={`share-editor-background-swatch ${
                      backgroundId === background.id ? "is-selected" : ""
                    }`}
                    style={
                      background.kind === "image"
                        ? {
                            backgroundImage: `url(${background.src})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }
                        : { background: background.css }
                    }
                    onClick={() => setBackgroundId(background.id)}
                    title={background.name}
                    aria-label={`Use ${background.name} background`}
                  />
                ))}
                {customBackground && (
                  <button
                    type="button"
                    className={`share-editor-background-swatch share-editor-background-custom ${
                      backgroundId === "custom" ? "is-selected" : ""
                    }`}
                    style={{
                      backgroundImage: `url(${customBackground.src})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                    }}
                    onClick={() => setBackgroundId("custom")}
                    title={customBackground.name}
                    aria-label="Use uploaded background"
                  />
                )}
              </div>
              <Form.Control
                className="mt-2"
                size="sm"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={handleBackgroundUpload}
                aria-label="Upload custom background"
              />

              <Form.Label className="share-editor-label mt-3">
                Blur <span>{Math.round(backgroundBlur)}px</span>
              </Form.Label>
              <Form.Range
                min={0}
                max={24}
                step={1}
                value={backgroundBlur}
                onChange={(event) => setBackgroundBlur(Number(event.target.value))}
              />

              <Form.Label className="share-editor-label">
                Darken <span>{Math.round(backgroundDarkness * 100)}%</span>
              </Form.Label>
              <Form.Range
                min={0}
                max={0.7}
                step={0.01}
                value={backgroundDarkness}
                onChange={(event) => setBackgroundDarkness(Number(event.target.value))}
              />

              <Form.Label className="share-editor-label">
                Vignette <span>{Math.round(vignetteStrength * 100)}%</span>
              </Form.Label>
              <Form.Range
                min={0}
                max={1}
                step={0.01}
                value={vignetteStrength}
                onChange={(event) => setVignetteStrength(Number(event.target.value))}
              />
            </section>

            <section>
              <div className="share-editor-control-title">Elements</div>
              <Form.Select
                size="sm"
                className="mb-3"
                value={selectedId || ""}
                onChange={(event) => {
                  selectAndRaise(event.target.value);
                }}
              >
                {elements.map((element) => (
                  <option value={element.id} key={element.id}>
                    {getElementDisplayLabel(element)}
                  </option>
                ))}
              </Form.Select>

              <div className="d-grid gap-2 mb-3">
                <Button variant="outline-primary" size="sm" onClick={addTextElement}>
                  Add custom text
                </Button>
                {selectedElement?.kind === "text" && (
                  <Button variant="outline-danger" size="sm" onClick={removeSelectedText}>
                    Delete selected text
                  </Button>
                )}
              </div>

              {selectedElement?.kind === "card" && (
                <>
                  <Form.Label className="share-editor-label">
                    Rotation <span>{Math.round(selectedElement.rotation)}°</span>
                  </Form.Label>
                  <Form.Range
                    min={-35}
                    max={35}
                    step={1}
                    value={selectedElement.rotation}
                    onChange={(event) =>
                      updateElement(selectedElement.id, {
                        rotation: Number(event.target.value),
                      })
                    }
                  />

                  <Form.Label className="share-editor-label">
                    Size <span>{Math.round(selectedElement.scale * 100)}%</span>
                  </Form.Label>
                  <Form.Range
                    min={0.35}
                    max={1.35}
                    step={0.01}
                    value={selectedElement.scale}
                    onChange={(event) =>
                      updateElement(selectedElement.id, {
                        scale: Number(event.target.value),
                      })
                    }
                  />
                </>
              )}

              {selectedElement?.kind === "text" && (
                <>
                  <Form.Label className="share-editor-label">Text content</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    className="mb-3"
                    value={selectedElement.text}
                    onChange={(event) =>
                      updateElement(selectedElement.id, {
                        text: event.target.value,
                      })
                    }
                  />

                  <Form.Label className="share-editor-label">
                    Rotation <span>{Math.round(selectedElement.rotation)}°</span>
                  </Form.Label>
                  <Form.Range
                    min={-180}
                    max={180}
                    step={1}
                    value={selectedElement.rotation}
                    onChange={(event) =>
                      updateElement(selectedElement.id, {
                        rotation: Number(event.target.value),
                      })
                    }
                  />

                  <Form.Label className="share-editor-label">
                    Size <span>{Math.round(selectedElement.fontSize)}px</span>
                  </Form.Label>
                  <Form.Range
                    min={18}
                    max={180}
                    step={1}
                    value={selectedElement.fontSize}
                    onChange={(event) =>
                      updateElement(selectedElement.id, {
                        fontSize: Number(event.target.value),
                      })
                    }
                  />

                  <Form.Label className="share-editor-label">Text color</Form.Label>
                  <Form.Control
                    type="color"
                    value={selectedElement.color || "#ffffff"}
                    onChange={(event) =>
                      updateElement(selectedElement.id, {
                        color: event.target.value,
                      })
                    }
                  />
                </>
              )}

              {selectedElement && (
                <div className="share-editor-nudge-grid" aria-label="Nudge selected element">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => nudgeSelected(0, -10)}
                  >
                    ↑
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => nudgeSelected(-10, 0)}
                  >
                    ←
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => nudgeSelected(10, 0)}
                  >
                    →
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => nudgeSelected(0, 10)}
                  >
                    ↓
                  </Button>
                </div>
              )}
            </section>

            <section>
              <div className="share-editor-control-title">Details</div>
              <Form.Check
                type="switch"
                id="share-editor-title-toggle"
                label="Show Card Title"
                checked={showTitle}
                onChange={(event) => setShowTitle(event.target.checked)}
              />
              <Form.Check
                type="switch"
                id="share-editor-brand-toggle"
                label="Show HearthCards"
                checked={showBrand}
                onChange={(event) => setShowBrand(event.target.checked)}
              />
            </section>

          </aside>
        </div>

        {exportError && <div className="alert alert-danger mt-3 mb-0">{exportError}</div>}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={exportImage} disabled={isExporting}>
          {isExporting ? "Rendering…" : "Download PNG"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ShareCardEditor;
